/**
 * MTG Mana Symbol Utilities
 * Uses Scryfall's official SVG symbols
 */

// Base URL for Scryfall mana symbols
const SCRYFALL_SYMBOLS_BASE = 'https://svgs.scryfall.io/card-symbols';

/**
 * Get the SVG URL for a mana symbol
 * @param symbol - The mana symbol (e.g., 'W', 'U', 'B', 'R', 'G', 'C', '1', '2', 'WU', 'WP', etc.)
 */
export function getManaSymbolUrl(symbol: string): string {
    return `${SCRYFALL_SYMBOLS_BASE}/${symbol.toUpperCase()}.svg`;
}

// Common mana symbol URLs
export const MANA_SYMBOLS = {
    // Basic colors
    W: getManaSymbolUrl('W'),
    U: getManaSymbolUrl('U'),
    B: getManaSymbolUrl('B'),
    R: getManaSymbolUrl('R'),
    G: getManaSymbolUrl('G'),
    C: getManaSymbolUrl('C'), // Colorless

    // Generic/numeric (0-20)
    ...Object.fromEntries(
        Array.from({ length: 21 }, (_, i) => [i.toString(), getManaSymbolUrl(i.toString())])
    ),
    X: getManaSymbolUrl('X'),

    // Hybrid mana
    WU: getManaSymbolUrl('WU'),
    WB: getManaSymbolUrl('WB'),
    UB: getManaSymbolUrl('UB'),
    UR: getManaSymbolUrl('UR'),
    BR: getManaSymbolUrl('BR'),
    BG: getManaSymbolUrl('BG'),
    RG: getManaSymbolUrl('RG'),
    RW: getManaSymbolUrl('RW'),
    GW: getManaSymbolUrl('GW'),
    GU: getManaSymbolUrl('GU'),

    // Phyrexian mana
    WP: getManaSymbolUrl('WP'),
    UP: getManaSymbolUrl('UP'),
    BP: getManaSymbolUrl('BP'),
    RP: getManaSymbolUrl('RP'),
    GP: getManaSymbolUrl('GP'),

    // 2/Color hybrid
    '2W': getManaSymbolUrl('2W'),
    '2U': getManaSymbolUrl('2U'),
    '2B': getManaSymbolUrl('2B'),
    '2R': getManaSymbolUrl('2R'),
    '2G': getManaSymbolUrl('2G'),

    // Snow and Energy
    S: getManaSymbolUrl('S'),
    E: getManaSymbolUrl('E'),
} as const;

// Color pip types
export type ColorPip = 'W' | 'U' | 'B' | 'R' | 'G';
export type ManaPip = ColorPip | 'C';

export interface ParsedManaCost {
    totalMV: number;
    generic: number;
    pips: {
        W: number;
        U: number;
        B: number;
        R: number;
        G: number;
        C: number; // Colorless (Eldrazi-style)
    };
    original: string;
}

/**
 * Parse a mana cost string into its components
 * Handles both simplified format (2W, 1GG) and Scryfall format ({2}{W})
 * Properly handles hybrid mana: {U/B} = 1 MV (not 2), tracks both colors
 * 
 * @param cost - The mana cost string (e.g., "2W", "1GG", "{2}{W}{W}", "{4}{U/B}{U/B}")
 * @param fallbackMV - Fallback MV if parsing fails
 */
export function parseManaCost(cost: string, fallbackMV = 0): ParsedManaCost {
    if (!cost || cost.trim() === '') {
        return {
            totalMV: fallbackMV,
            generic: fallbackMV,
            pips: { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 },
            original: fallbackMV.toString(),
        };
    }

    // Strip adventure/split "Front // Back" cost - only parse front face
    const frontCost = cost.includes(' // ') ? cost.split(' // ')[0] : cost;

    let generic = 0;
    const pips = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
    let pipSlots = 0; // Each colored/hybrid symbol = 1 MV slot

    const colorKeys = 'WUBRGC';
    const addPip = (c: string) => {
        if (c === 'W') pips.W++;
        else if (c === 'U') pips.U++;
        else if (c === 'B') pips.B++;
        else if (c === 'R') pips.R++;
        else if (c === 'G') pips.G++;
        else if (c === 'C') pips.C++;
    };

    // Check for Scryfall brace format: {4}{U/B}{W}
    const braceTokens = frontCost.match(/\{[^}]+\}/g);

    if (braceTokens && braceTokens.length > 0) {
        // Parse Scryfall format token by token
        for (const token of braceTokens) {
            const inner = token.slice(1, -1).toUpperCase();

            if (/^\d+$/.test(inner)) {
                // Generic mana: {4}, {2}, etc.
                generic += parseInt(inner);
            } else if (inner === 'X') {
                // X costs: 0 MV contribution
            } else if (inner.includes('/')) {
                // Hybrid: {U/B}, {2/W}, {W/P}, etc. — always 1 MV
                pipSlots++;
                for (const part of inner.split('/')) {
                    if (colorKeys.includes(part)) addPip(part);
                    // Ignore numeric parts (2 in {2/W}) and P (Phyrexian life cost)
                }
            } else {
                // Single color: {W}, {U}, etc.
                pipSlots++;
                for (const c of inner) {
                    if (colorKeys.includes(c)) addPip(c);
                }
            }
        }
    } else {
        // Parse simplified format: 4UBB or 4U/BU/B
        let remaining = frontCost.toUpperCase().trim();

        // If it's just a number, return that
        if (/^\d+$/.test(remaining)) {
            const mv = parseInt(remaining);
            return {
                totalMV: mv,
                generic: mv,
                pips: { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 },
                original: cost,
            };
        }

        // Extract hybrid patterns FIRST (X/Y where X,Y are single chars)
        // This prevents U/B from being counted as U + B (2 pips) instead of 1 hybrid pip
        const hybridRegex = /([WUBRGC2])\/([WUBRGCP])/g;
        let match;
        while ((match = hybridRegex.exec(remaining)) !== null) {
            pipSlots++;
            const [, left, right] = match;
            if (colorKeys.includes(left)) addPip(left);
            if (colorKeys.includes(right)) addPip(right);
        }
        // Remove hybrid patterns from remaining string
        remaining = remaining.replace(/[WUBRGC2]\/[WUBRGCP]/g, '');

        // Extract leading generic mana
        const genericMatch = remaining.match(/^(\d+)/);
        if (genericMatch) {
            generic = parseInt(genericMatch[1]);
        }

        // Count remaining single color pips
        const colorPart = remaining.replace(/^\d+/, '');
        for (const char of colorPart) {
            if (colorKeys.includes(char)) {
                addPip(char);
                pipSlots++;
            }
        }
    }

    const totalMV = generic + pipSlots;

    return {
        totalMV,
        generic,
        pips,
        original: cost,
    };
}

/**
 * Format a parsed mana cost back to a simple string (e.g., "2WW")
 */
export function formatManaCost(parsed: ParsedManaCost): string {
    let result = '';
    if (parsed.generic > 0) result += parsed.generic.toString();
    result += 'W'.repeat(parsed.pips.W);
    result += 'U'.repeat(parsed.pips.U);
    result += 'B'.repeat(parsed.pips.B);
    result += 'R'.repeat(parsed.pips.R);
    result += 'G'.repeat(parsed.pips.G);
    result += 'C'.repeat(parsed.pips.C);
    return result || '0';
}

/**
 * Convert Scryfall mana cost format to simple format
 * {2}{W}{W} -> 2WW
 */
export function scryfallToSimple(manaCost: string): string {
    if (!manaCost) return '';
    // Strip adventure/split portion
    const frontCost = manaCost.includes(' // ') ? manaCost.split(' // ')[0] : manaCost;
    return frontCost.replace(/\{([^}]+)\}/g, '$1').toUpperCase();
}

/**
 * Get the colors present in a mana cost
 */
export function getColorsInCost(cost: string): ColorPip[] {
    const parsed = parseManaCost(cost);
    const colors: ColorPip[] = [];
    if (parsed.pips.W > 0) colors.push('W');
    if (parsed.pips.U > 0) colors.push('U');
    if (parsed.pips.B > 0) colors.push('B');
    if (parsed.pips.R > 0) colors.push('R');
    if (parsed.pips.G > 0) colors.push('G');
    return colors;
}

/**
 * Check if a cost requires a specific color
 */
export function requiresColor(cost: string, color: ColorPip): boolean {
    const parsed = parseManaCost(cost);
    return parsed.pips[color] > 0;
}

/**
 * Get the pip requirement for a specific color
 */
export function getColorRequirement(cost: string, color: ColorPip): number {
    const parsed = parseManaCost(cost);
    return parsed.pips[color];
}
